import styled from '@emotion/styled';
import { Col, Row } from 'antd';
import type { FC } from 'react';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import { useEventFieldGenerator } from 'src/lib/customHooks/useEventFieldGenerator';
import { PopoverHint } from '../BasicComponents';
import { BlurFormInput } from '../FormComponents';
import {
  DistanceFromTrack,
  JumpingDotThresehold,
  RSSIThresehold,
} from './PredictionSetupAnimations';

export const ParametersSubTab: FC = () => {
  const event = useCurrentEvent();
  const fieldGenerator = useEventFieldGenerator(event);

  if (fieldGenerator == null || event == null) return <></>;

  return (
    <Container>
      Predictive live tracking involves the below parameters. Set these parameters carefully, as
      they impact the quality of your prediction. The suggested values are based on the best results
      we have seen in the past. Usually the default values are the best choice.
      <Row gutter={[16, 16]} style={{ marginTop: '10px' }}>
        <Col span={8}>
          <JumpingDotThresehold />
          <BlurFormInput
            id="racemap_event_predictive_jumping_dots_threshold_override"
            type="number"
            label={
              <>
                Jumping dot threshold in seconds
                <PopoverHint>
                  With a new read, there is a new speed calculated for a predicted virtual
                  participant (represented by location of dot in tracking map). The "Jumping dot
                  threshold" is the maximum time difference between the latest read and the virtual
                  participant. If this difference is greater, the virtual participant jumps to the
                  location of the latest read. If this difference is smaller, the virtual
                  participant will either move faster or slower to smoothly reduce the prediction
                  error during the ongoing event. A value of <strong>0.0</strong> disables the
                  feature - no jumps will be done.
                  <br />
                  <strong>Suggested Value: 360&thinsp;s</strong>
                </PopoverHint>
              </>
            }
            {...fieldGenerator('modules.predictive.params.jumpingDotThreshold')}
            step="60"
          />
          <SuggestedValue value={'360 s'} />
        </Col>
        <Col span={8}>
          <DistanceFromTrack />
          <BlurFormInput
            id="racemap_event_predictive_distance_to_track_override"
            type="number"
            label={
              <>
                Max distance from track in meters
                <PopoverHint>
                  Maximum distance between shadowtrack and reader in which reads can be considered
                  for prediction. Readers with a greater distance from the shadowtrack are not
                  considered for prediction and are shown as "Out of range". If the distance is
                  50&thinsp;m the width of the whole corridor is 100&thinsp;m.
                  <br />
                  <strong>Suggested Value: 50&thinsp;m</strong>
                </PopoverHint>
              </>
            }
            {...fieldGenerator('modules.predictive.params.distanceFromTrack')}
            step="1.0"
          />
          <SuggestedValue value={'50 m'} />
        </Col>
        <Col span={8}>
          <RSSIThresehold />
          <BlurFormInput
            id="racemap_event_predictive_distance_to_track_override"
            type="number"
            label={
              <>
                RSSI Threshold in dBm
                <PopoverHint>
                  With Track Boxes the reads have a Received Signal Strength Indicator (RSSI). The
                  RSSI threshold is the minimal value to be considered for prediction. Reads with a
                  smaller RSSI are not considered for prediction, as small values indicate a
                  significant distance between Track Box and transponder. Allowing small RSSI values
                  increases possible errors as the transponder could be detected by multiple Track
                  Boxes over a long distance at nearly the same time.
                  <br />
                  <strong>Suggested Value: -75</strong>
                </PopoverHint>
              </>
            }
            {...fieldGenerator('modules.predictive.params.rssiThreshold')}
            step="1.0"
          />
          <SuggestedValue value={'-75 dBm'} />
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: '10px' }}>
        <Col span={8}>
          <BlurFormInput
            id="racemap_event_predictive_future_track_scale_value_override"
            type="number"
            label={
              <>
                Future track scale value
                <PopoverHint>
                  The relative distance in which the predicted virtual participant "meets" the
                  participant moving along with the measured average speed. The greater the track
                  scale value the longer takes the correction.
                  <br />
                  <strong>Suggested Value: 1.5</strong>
                </PopoverHint>
              </>
            }
            {...fieldGenerator('modules.predictive.params.futureTrackScaleValue')}
            step="0.1"
          />
          <SuggestedValue value={1.5} />
        </Col>
        <Col span={8}>
          <BlurFormInput
            id="racemap_event_predictive_future_track_scale_value_override"
            type="number"
            label={
              <>
                Look back distance in m
                <PopoverHint>
                  The look back distance makes prediction less susceptible to measurement errors.
                  With a new read, there is a new speed calculated for a predicted virtual
                  participant. The prediction might receive many reads from a short section of the
                  track. These reads can have location errors and therefore cause malicious
                  velocities. The look back distance is that length for calculating an average speed
                  value for the prediction.
                  <br />
                  <strong>Suggested Value: 1000&thinsp;m</strong>
                </PopoverHint>
              </>
            }
            {...fieldGenerator('modules.predictive.params.lookBackDistance')}
            step="0.1"
          />
          <SuggestedValue value={'1000 m'} />
        </Col>
        <Col span={8}>
          <BlurFormInput
            id="racemap_event_predictive_jumping_dots_threshold_override"
            type="number"
            label={
              <>
                Look Forward Time in seconds
                <PopoverHint>
                  The look forward time compensates for the delay in data transmission and
                  processing throughout our systems.
                  <br />
                  <strong>Suggested Value: 15&thinsp;s</strong>
                </PopoverHint>
              </>
            }
            {...fieldGenerator('modules.predictive.params.lookForwardTime')}
            step="60"
          />
          <SuggestedValue value={'15 s'} />
        </Col>
      </Row>
    </Container>
  );
};

const Container = styled.div`
  padding: 30px 25px;

  .bordered-box {
    margin-bottom: 20px;
    padding: 15px;
  }
`;

const SuggestedValue = ({ value }: { value: number | string | null }) => {
  return <SuggestedContainer>Suggested value: {value ? value.toString() : ''}</SuggestedContainer>;
};

const SuggestedContainer = styled.div`
margin-top: -13px;
  font-size: 10px;
`;
